import { Injectable, ErrorHandler, Inject } from "@angular/core";
import { BehaviorSubject, Subject, Observable, throwError } from "rxjs";
import { Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { map, catchError } from "rxjs/operators";
import axios from "axios";
import { AxiosInstance } from "axios";
import { Users } from "../core/model/user";
import * as CryptoJS from 'crypto-js';
import { apiLinks } from "../core/api-links/api-links";
import { Modules } from "../core/model/modules.enum";
import { get, isEmpty } from "lodash";
import { DOCUMENT } from "@angular/common";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { InformationModalComponent } from "../shared/components/information-modal/information-modal.component";
import { FormGroup, Validators } from "@angular/forms";
import { MENU } from "../views/layout/sidebar/menu";
import { SubAppWindowBridgeService } from "../core/services/sub-app-window-bridge.service";

const PORTAL_THEME_CLASSES = [
  'theme-cyan-light',
  'theme-bluegrey-light',
  'theme-purple-light',
] as const;

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private currentUser = new BehaviorSubject(null);
  private logoutUrl = `${environment.apiUrl}/user-logout`;
  private userUrl = `${environment.apiUrl}/users`;
  private axiosClient: AxiosInstance;
  private errorHandler: ErrorHandler;

  constructor(private router: Router, private httpClient: HttpClient, @Inject(DOCUMENT) private document: Document,
  private ngbModal: NgbModal,
  private subAppWindowBridge: SubAppWindowBridgeService
) {
    const getUser = localStorage.getItem("current_user");
    this.currentUser.next(JSON.parse(getUser));
  }

  get getUser() {
    return this.currentUser.value;
  }

  setUser(user) {
    try {
      const prevRaw = localStorage.getItem("current_user");
      const prev = prevRaw ? JSON.parse(prevRaw) : null;
      if (this.didPortalSessionIdentityChange(prev, user)) {
        this.notifyDejaupWindowsLogout();
      }
    } catch {
      /* ignore */
    }
    localStorage.setItem("current_user", JSON.stringify(user));
    this.currentUser.next(user);
    return user;
  }

  /** Clear MFA / TOTP pending session without full logout side-effects. */
  clearPendingAuthUser(): void {
    localStorage.removeItem("current_user");
    this.currentUser.next(null);
  }

  /** True when another account replaces the stored Portal session (Dejaup must drop the previous SSO context). */
  private didPortalSessionIdentityChange(prev: any, next: any): boolean {
    if (!next) return false;
    if (!prev?.accessToken) return false;
    const key = (u: any) => `${String(u?.id ?? "")}|${String(u?.email ?? "").toLowerCase()}`;
    return key(prev) !== key(next);
  }

  goToForgotPassword() {
    this.router.navigate(['/auth/forgot-password']);
  }

  updateUser(newData) {
    let user={
      ...this.getUser,
      ...newData
    };
    localStorage.removeItem("current_user");
    localStorage.setItem("current_user", JSON.stringify(user));
    this.currentUser.next(user);
    return user;
  }

  /**
   * Tell open Dejaup tabs (postMessage) to log out. Call before server `user-logout` when possible.
   */
  notifyDejaupWindowsLogout(): void {
    this.subAppWindowBridge.broadcastLogoutToVmsWindows();
  }

  logout(message: string = '') {
    this.notifyDejaupWindowsLogout();
    let theme = JSON.parse(localStorage.getItem("current_user"))?.themeColor;
    localStorage.removeItem("logger_token");
    localStorage.removeItem("current_user");
    localStorage.setItem("ip", '');
    localStorage.setItem("port", '');
    localStorage.setItem("generatedTpn", '');
    localStorage.setItem("generatedToken", '');
    localStorage.setItem("generatedTapTpn", '');
    localStorage.setItem("generatedTapToken", '');
    this.currentUser.next(null);
    localStorage.setItem("defaultTheme", theme);
    this.router.navigate(["/auth/login"], { queryParams: { message } });
  }

  async logoutService() {
    this.notifyDejaupWindowsLogout();

    const proxyId = this.currentUser.value.proxyLogId
    if (proxyId) {
      await this.httpClient.put(`${apiLinks.setProxyLogOutTime}?id=${proxyId}`, {}).toPromise().then(res => { })
    }
    return this.httpClient.delete(`${this.logoutUrl}`).pipe(
      map((res) => {
        this.logout();
        return res;
      })
    );
  }

  updateMfaStatus = (body: any) => {
    return this.httpClient.post(`${apiLinks.mfa.updateStatus}`, body);
  }
 getModuleGroupMap(): Record<string, string[]> {
  const groupMap: Record<string, Set<string>> = {};

  for (const item of MENU) {
    if (item.moduleGroup) {
      if (!groupMap[item.moduleGroup]) {
        groupMap[item.moduleGroup] = new Set();
      }
      groupMap[item.moduleGroup].add(item.module);
    }
  }

  // Convert sets to arrays
  return Object.entries(groupMap).reduce((acc, [group, modules]) => {
    acc[group] = Array.from(modules);
    return acc;
  }, {} as Record<string, string[]>);
}

getPrivileges(moduleName: string) {
  if (!this.getUser) return false;

  const roleFeatures = this.getUser.roleFeatures;
  const moduleGroupMap = this.getModuleGroupMap();

  // Step 1: Check if the module belongs to a group
  const groupKey = Object.keys(moduleGroupMap).find(group =>
    moduleGroupMap[group].includes(moduleName)
  );

  if (groupKey) {
    // Step 2: Collect all features for all modules in the group
    const groupModules = moduleGroupMap[groupKey];
    const mergedFeatures = groupModules.flatMap(mod =>
      roleFeatures.find(rf => rf.module === mod)?.features || []
    );
    const excludeFeatures = this.getUser?.excludeFeatures || [];
    const filteredFeatures = [...new Set(mergedFeatures)].filter(
      f => !excludeFeatures.includes(f)
    );

    return {
      module: groupKey,
      features: filteredFeatures,
    };
  }

  // Step 3: Fallback to single module if not in group
  const singleModule = roleFeatures.find(rf => rf.module === moduleName);
  if (!singleModule) return false;
  const excludeFeatures = this.getUser?.excludeFeatures || [];
  const rawFeatures = singleModule.features || [];
  const filteredFeatures = rawFeatures.filter(f => !excludeFeatures.includes(f));
  return { ...singleModule, features: filteredFeatures };
}


  isFeatureEnabled(moduleName, featureName) {

    if(this.getPrivileges(moduleName)){
      let featureList = this.getPrivileges(moduleName).features;
      return featureList.indexOf(featureName) > -1 ? true : false;
    }else{
      return false;
    }
   

  }
  getUserType() {
    return this.getUser.userType;
  }
  getCustomerID() {
    return this.getUser?.isoIdentified;
  }
  getUserRoleType(){
    return {
      userType: this.getUserType(),
      userTypeRole: this.getUser?.userTypeRole,
    };
  }
  get allowSpecialFeature(): boolean {
    return Boolean(get(JSON.parse(localStorage.getItem('current_user')), 'allowSpecialFeature'));
  }

  handleError(error) {
    let errorMsg = {};

    if (error instanceof ErrorEvent) {
      errorMsg["status"] = error.error.status;
      errorMsg["message"] = error.error.message;
    } else {
      errorMsg["status"] = error.status;
      errorMsg["message"] = error.error;
    }

    return throwError(errorMsg);
  }

  refreshToken(token): Observable<any> {
    return;
  }



  encryptData(data) {

    try {
      return CryptoJS.AES.encrypt(JSON.stringify(data), environment.secretKey).toString();
    } catch (e) {
      console.log(e);
    }

  }

  decryptData(data) {

    try {
      const bytes = CryptoJS.AES.decrypt(data, environment.secretKey);

      if (bytes.toString()) {

        return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
      }
      return data;
    } catch (e) {
      console.log(e);
    }

  }

  subIsoToAgentOffice(item){
    if (!item) {
      return '';
    }

    //console.log(this.authService.getUser.userType);
    const searchRegExp = /\b(?:Sub Iso|SUB_ISO)\b/gi;
    
    const replaceWith = 'Agent Office';
    const result = item.replace(searchRegExp, replaceWith);
    
    return result;

  }

  assignPrivileges(module: Modules, access) {
    const userAccess = this.getPrivileges(module);
    if (isEmpty(userAccess)) {
      return access
    }
    const features = userAccess?.features as string[]
    for (const key of Object.keys(access)) {
      if (features.includes(key)) {
        access[key] = true
      }
    }
    return access
  }

   getSecurityCheck() {  
     return this.httpClient.get(`${apiLinks.securityCheck}`).pipe(
        map((res:any) => {
          localStorage.setItem("terms_allow_user", JSON.stringify(res?.termsUser));
          return res?.data;
        })
      );
     }
     setCaptchaValidators(form: FormGroup, enableCaptcha: boolean){
      if (enableCaptcha) {
        // (window as any).handleCaptchaResponse = this.handleCaptchaResponse.bind(this);
        // const script = document.createElement('script');
        // script.src = 'https://www.google.com/recaptcha/enterprise.js';
        // script.async = true;
        // script.defer = true;
        // document.body.appendChild(script);
        form.get('captchaToken').setValidators([Validators.required]);
        form.get('captchaToken').updateValueAndValidity();
      } else {
        form.get('captchaToken').setValidators(null);
        form.get('captchaToken').setValue(null);
        form.get('captchaToken').updateValueAndValidity();
      }
     }

    defaultTheme(){
      this.applyPortalTheme(localStorage.getItem('defaultTheme'));
    }

    /**
     * Apply portal auth theme on body and persist for the next login screen.
     * Skips DOM updates when the theme is already active (avoids TOTP transition flicker).
     */
    applyPortalTheme(themeColor?: string | null): string {
      const theme = themeColor?.trim()
        ? themeColor
        : (localStorage.getItem('defaultTheme') || 'theme-purple-light');
      localStorage.setItem('defaultTheme', theme);
      const { body } = this.document;
      if (body.classList.contains(theme)) {
        return theme;
      }
      PORTAL_THEME_CLASSES.forEach((cls) => body.classList.remove(cls));
      body.classList.add(theme);
      return theme;
    }

    showModal = (data: any) => {
      let modalRef = this.ngbModal.open(InformationModalComponent, { ariaLabelledBy: 'modal-basic-title', centered: true, backdrop: 'static', keyboard: false });
      modalRef.componentInstance.header = data?.header || "";
      modalRef.componentInstance.body = data?.message || "something went wrong !";
      modalRef.componentInstance.htmlContent = data?.bodyData || "";
      modalRef.componentInstance.successFlag = false;
      modalRef.componentInstance.okButton = true;
      return modalRef;
    }      
     resetCaptcha(form: FormGroup, captchaComponent: any, enableCaptcha: boolean) {
      setTimeout(() => {
        if (enableCaptcha) {
          form.get('captchaToken').reset();
          captchaComponent?.reset();
        } else {
          console.log('reCAPTCHA not available');
        }
      });
    }
  
    resolved(form: FormGroup, captchaResponse: string) {
      form.get('captchaToken').setValue(captchaResponse);
    }
}
